import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { getSocket } from '@/lib/socket'

// ─────────────────────────────────────────────────────────────────────────────
// Kéo lịch sử tin nhắn (backfill) cho tài khoản Zalo — chạy nền ở backend, tiến
// độ đẩy về qua socket `zalo:backfill-progress`.
// ─────────────────────────────────────────────────────────────────────────────

export interface BackfillProgress {
  accountId: string
  current: number
  total: number
  threadName?: string
  status: 'processing' | 'completed'
  mode?: 'friends'
  result?: { totalInserted?: number; totalSkipped?: number; withHistory?: number; errors?: number }
}

/** Bắt đầu kéo lịch sử cho một tài khoản (mọi hội thoại; kèm bạn bè nếu chọn). */
export function useBackfillAccount() {
  return useMutation<
    { message: string; accountId: string; includeFriends: boolean },
    unknown,
    { accountId: string; maxMessages?: number; includeFriends?: boolean }
  >({
    mutationFn: async ({ accountId, maxMessages, includeFriends }) => {
      const { data } = await api.post(`/zalo-accounts/${accountId}/backfill`, {
        maxMessages,
        includeFriends,
      })
      return data
    },
  })
}

/**
 * Theo dõi tiến độ backfill của một tài khoản. Tiến độ nay LƯU DB ở máy chủ (giai đoạn 2) nên lúc mở/tải lại trang
 * vẫn tải được job đang chạy hoặc vừa xong qua REST — trước đây chỉ có qua socket, F5 là mất, tưởng đã xong hoặc hỏng.
 * Socket vẫn dùng để cập nhật mượt cho tab đang mở sẵn.
 */
export function useBackfillProgress(accountId: string): BackfillProgress | null {
  const [progress, setProgress] = useState<BackfillProgress | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .get<BackfillProgress | { accountId: string; status: null }>(`/zalo-accounts/${accountId}/backfill/status`)
      .then(({ data }) => {
        if (cancelled || data.status === null) return
        const p = data as BackfillProgress
        setProgress(p)
        if (p.status === 'completed') {
          setTimeout(() => setProgress((cur) => (cur?.status === 'completed' ? null : cur)), 6000)
        }
      })
      .catch(() => {}) // không có job nào / lỗi mạng — coi như chưa từng chạy, không chặn giao diện
    return () => {
      cancelled = true
    }
  }, [accountId])

  useEffect(() => {
    const socket = getSocket()
    const onProgress = (p: BackfillProgress) => {
      if (p.accountId !== accountId) return
      setProgress(p)
      // Xong thì giữ kết quả một lát rồi ẩn để nút về trạng thái thường.
      if (p.status === 'completed') {
        setTimeout(() => setProgress((cur) => (cur?.status === 'completed' ? null : cur)), 6000)
      }
    }
    socket.on('zalo:backfill-progress', onProgress)
    return () => {
      socket.off('zalo:backfill-progress', onProgress)
    }
  }, [accountId])

  return progress
}
